
 // src/components/Navbar.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import nulekLogo from "../images/logos/NulekLogob6.gif";
import styles from "./Navbar.module.css";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownTimeoutRef = useRef(null);

  const services = [
    { name: "Planning & Scheduling", path: "/services/PlanningScheduling/" },
    { name: "Schedule Risk Analysis", path: "/services/ScheduleRiskAnalysis/" },
    { name: "Time–Chainage Diagram Development", path: "/services/TimeChainage" },
    { name: "Power BI Dashboard & Reporting Solutions", path: "/services/Report" },
    { name: "Training", path: "/services/training" },
    { name: "All Services", path: "/services/" },
  ];

  // Articles submenu
  const articles = [
    {
      name: "Earned Value Management",
      path: "/blog?category=Earned%20Value%20Management",
    },
    {
      name: "Primavera P6",
      path: "/blog?category=Primavera%20P6",
    },
    {
      name: "Risk Management",
      path: "/blog?category=Risk%20Management",
    },
    {
      name: "Project Management",
      path: "/blog?category=Project%20Management",
    },
    {
      name: "Planning & Scheduling",
      path: "/blog?category=Planning%20%26%20Scheduling",
    },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();

    window.addEventListener("resize", handleResize);

    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);

      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((previous) => !previous);
    setOpenDropdown(null);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (menu) => {
    setOpenDropdown((previous) => (previous === menu ? null : menu));
  };

  const handleMouseEnter = (item) => {
    if (!isMobile) {
      setHoveredItem(item);

      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
        dropdownTimeoutRef.current = null;
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) setHoveredItem(null);
  };

  const handleDropdownMouseLeave = () => {
    if (!isMobile) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
        setHoveredItem(null);
      }, 300);
    }
  };

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
  };

  // Reusable submenu for mobile
  const renderMobileSubmenu = (items) =>
    items.map((item) => (
      <Link
        key={item.name}
        to={item.path}
        className={styles.dropdownItemMobile}
        onClick={closeMobileMenu}
      >
        <span className={styles.dropdownDot} />
        {item.name}
      </Link>
    ));

  // Reusable submenu for desktop
  const renderDesktopSubmenu = (items) =>
    items.map((item) => (
      <Link
        key={item.name}
        to={item.path}
        className={`${styles.dropdownItemDesktop} ${
          hoveredItem === item.name ? styles.dropdownItemDesktopHover : ""
        }`}
        onMouseEnter={() => handleMouseEnter(item.name)}
        onMouseLeave={handleMouseLeave}
      >
        <span
          className={`${styles.dropdownArrow} ${
            hoveredItem === item.name ? styles.dropdownArrowVisible : ""
          }`}
        >
          →
        </span>
        {item.name}
      </Link>
    ));

  return (
    <>
      {/* Top Contact Bar */}
      <div className={`${styles.topBar} ${isMobile ? styles.topBarMobile : ""}`}>
        <div
          className={`container ${styles.topContainer} ${
            isMobile ? styles.topContainerMobile : ""
          }`}
        >
          <div
            className={`${styles.contactInfo} ${
              isMobile ? styles.contactInfoMobile : ""
            }`}
          >
            <span
              className={`${styles.contactItem} ${
                isMobile ? styles.contactItemMobile : ""
              }`}
            >
              <span className={styles.contactIcon}>✉</span>
              <a
                href="mailto:info@nulekconsulting.com.au"
                className={styles.link}
              >
                info@nulekconsulting.com.au
              </a>
            </span>

            <span
              className={`${styles.contactItem} ${
                isMobile ? styles.contactItemMobile : ""
              }`}
            >
              <span
                className={styles.contactIcon}
                style={{
                  color: "#ffffff",
                  filter: "brightness(0) invert(1)",
                  opacity: 0.9,
                }}
              >
                📞
              </span>
              <div>+61 413 613 265</div>
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`${styles.navbar} ${
          scrolled ? styles.navbarScrolled : ""
        }`}
      >
        <div className={styles.redAccentLine} />

        <div className={`container ${styles.navContainer}`}>
          {/* Logo */}
          <Link to="/" className={styles.logoContainer}>
            <img src={nulekLogo} alt="Nulek" className={styles.logo} />
            <span className={styles.logoText}>consulting</span>
          </Link>

          {/* Hamburger */}
          {isMobile && (
            <button
              type="button"
              onClick={toggleMobileMenu}
              className={styles.hamburger}
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span
                className={`${styles.bar} ${
                  isMobileMenuOpen ? styles.barOpen1 : ""
                }`}
              />
              <span
                className={`${styles.bar} ${
                  isMobileMenuOpen ? styles.barOpen2 : ""
                }`}
              />
              <span
                className={`${styles.bar} ${
                  isMobileMenuOpen ? styles.barOpen3 : ""
                }`}
              />
            </button>
          )}

          {/* Navigation List */}
          <ul
            className={
              isMobile
                ? `${styles.navList} ${
                    isMobileMenuOpen
                      ? styles.navListOpen
                      : styles.navListClosed
                  }`
                : styles.navListDesktop
            }
          >
            {/* MOBILE NAVIGATION */}
            {isMobile ? (
              <>
                <li className={styles.navItem}>
                  <Link to="/" className={styles.navLink} onClick={closeMobileMenu}>
                    Home
                  </Link>
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/about"
                    className={styles.navLink}
                    onClick={closeMobileMenu}
                  >
                    About
                  </Link>
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/products/"
                    className={styles.navLink}
                    onClick={closeMobileMenu}
                  >
                    Products
                  </Link>
                </li>

                {/* Services submenu */}
                <li className={styles.navItem}>
                  <button
                    type="button"
                    className={styles.navLinkDropdown}
                    onClick={() => toggleDropdown("services")}
                    aria-expanded={openDropdown === "services"}
                  >
                    Services
                    <span
                      className={`${styles.chevron} ${
                        openDropdown === "services"
                          ? styles.chevronOpen
                          : ""
                      }`}
                    >
                      ▾
                    </span>
                  </button>

                  {openDropdown === "services" && (
                    <div className={styles.dropdownMenuMobile}>
                      {renderMobileSubmenu(services)}
                    </div>
                  )}
                </li>

                {/* Articles submenu */}
                <li className={styles.navItem}>
                  <button
                    type="button"
                    className={styles.navLinkDropdown}
                    onClick={() => toggleDropdown("articles")}
                    aria-expanded={openDropdown === "articles"}
                  >
                    Articles
                    <span
                      className={`${styles.chevron} ${
                        openDropdown === "articles"
                          ? styles.chevronOpen
                          : ""
                      }`}
                    >
                      ▾
                    </span>
                  </button>

                  {openDropdown === "articles" && (
                    <div className={styles.dropdownMenuMobile}>
                      {renderMobileSubmenu(articles)}
                    </div>
                  )}
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/contact"
                    className={styles.contactButtonMobile}
                    onClick={closeMobileMenu}
                  >
                    Contact Us
                  </Link>
                </li>
              </>
            ) : (
              /* DESKTOP NAVIGATION */
              <>
                <li className={styles.navItem}>
                  <Link
                    to="/"
                    className={`${styles.navLink} ${
                      hoveredItem === "home" ? styles.navLinkHover : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter("home")}
                    onMouseLeave={handleMouseLeave}
                  >
                    Home
                  </Link>
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/about"
                    className={`${styles.navLink} ${
                      hoveredItem === "about" ? styles.navLinkHover : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter("about")}
                    onMouseLeave={handleMouseLeave}
                  >
                    About
                  </Link>
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/products/"
                    className={`${styles.navLink} ${
                      hoveredItem === "products" ? styles.navLinkHover : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter("products")}
                    onMouseLeave={handleMouseLeave}
                  >
                    Products
                  </Link>
                </li>

                {/* Services dropdown */}
                <li
                  className={styles.dropdownWrapper}
                  onMouseEnter={() => {
                    setOpenDropdown("services");
                    handleMouseEnter("services");
                  }}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <span
                    className={`${styles.navLink} ${
                      hoveredItem === "services" ? styles.navLinkHover : ""
                    }`}
                  >
                    Services <span className={styles.chevron}>▾</span>
                  </span>

                  {openDropdown === "services" && (
                    <div
                      className={styles.dropdownMenuDesktop}
                      onMouseEnter={handleDropdownMouseEnter}
                      onMouseLeave={handleDropdownMouseLeave}
                    >
                      <div className={styles.dropdownRedBar} />
                      {renderDesktopSubmenu(services)}
                    </div>
                  )}
                </li>

                {/* Articles dropdown */}
                <li
                  className={styles.dropdownWrapper}
                  onMouseEnter={() => {
                    setOpenDropdown("articles");
                    handleMouseEnter("articles");
                  }}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <span
                    className={`${styles.navLink} ${
                      hoveredItem === "articles" ? styles.navLinkHover : ""
                    }`}
                  >
                    Articles <span className={styles.chevron}>▾</span>
                  </span>

                  {openDropdown === "articles" && (
                    <div
                      className={styles.dropdownMenuDesktop}
                      onMouseEnter={handleDropdownMouseEnter}
                      onMouseLeave={handleDropdownMouseLeave}
                    >
                      <div className={styles.dropdownRedBar} />
                      {renderDesktopSubmenu(articles)}
                    </div>
                  )}
                </li>

                <li className={styles.navItem}>
                  <Link
                    to="/contact"
                    className={`${styles.contactButtonDesktop} ${
                      hoveredItem === "contact"
                        ? styles.contactButtonDesktopHover
                        : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter("contact")}
                    onMouseLeave={handleMouseLeave}
                  >
                    Contact Us
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
